import { validateCnpj } from './cnpj.validator';
import { validateCpf } from './cpf.validator';
import { CpfCnpjValidationError } from '../models/validation-errors.model';

/**
 * Validates a Brazilian CPF or CNPJ number with auto-detection.
 * Detects type by digit count: 11 = CPF, 14 = CNPJ.
 * @param value - CPF or CNPJ string (with or without formatting)
 * @returns ValidationErrors with reason code and detected type, or null if valid
 */
export function validateCpfCnpj(value: string): CpfCnpjValidationError | null {
  if (!value) return null;

  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Detect type by length
  if (digits.length === 11) {
    // CPF
    const cpfError = validateCpf(digits);
    if (cpfError) {
      return {
        cpfCnpj: {
          reason: cpfError.cpf.reason,
          type: 'cpf',
          expected: cpfError.cpf.expected,
          actual: cpfError.cpf.actual,
        },
      };
    }
    return null;
  }

  if (digits.length === 14) {
    // CNPJ
    const cnpjError = validateCnpj(digits);
    if (cnpjError) {
      return {
        cpfCnpj: {
          reason: cnpjError.cnpj.reason,
          type: 'cnpj',
          expected: cnpjError.cnpj.expected,
          actual: cnpjError.cnpj.actual,
        },
      };
    }
    return null;
  }

  // Invalid length - not a valid CPF (11) or CNPJ (14)
  if (digits.length < 11) {
    return {
      cpfCnpj: {
        reason: 'length',
        type: 'unknown',
        expected: 11, // Assume trying to type CPF first
        actual: digits.length,
      },
    };
  }

  if (digits.length > 11 && digits.length < 14) {
    return {
      cpfCnpj: {
        reason: 'length',
        type: 'cnpj', // Between 11-14, likely typing CNPJ
        expected: 14,
        actual: digits.length,
      },
    };
  }

  // More than 14 digits
  return {
    cpfCnpj: {
      reason: 'length',
      type: 'unknown',
      expected: 14,
      actual: digits.length,
    },
  };
}
